import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Library as LibIcon, FileText, ShieldCheck, Plus, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Library() {
  const { user } = useAuth();
  const isAdmin = ["master_admin", "company_admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [acks, setAcks] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type: "pop", version: "1.0" });
  const [creating, setCreating] = useState(false);

  const fetchAll = async () => {
    const [docs, myAcks] = await Promise.all([
      api.get("/documents"),
      api.get("/acknowledgements/me"),
    ]);
    setItems(docs.data);
    setAcks(myAcks.data);
  };
  useEffect(() => { fetchAll(); }, []);

  const acknowledged = (id) => acks.some((a) => a.document_id === id);

  const acknowledge = async (id) => {
    await api.post("/acknowledgements", { document_id: id });
    toast.success("Ciência registrada");
    setOpenDoc(null);
    fetchAll();
  };

  const createDoc = async () => {
    await api.post("/documents", form);
    setCreating(false); setForm({ title: "", description: "", type: "pop", version: "1.0" });
    toast.success("Documento criado");
    fetchAll();
  };

  return (
    <div className="space-y-8" data-testid="library-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Biblioteca corporativa</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Documentos & POPs</h1>
          <p className="text-slate-600 mt-2 text-sm">Procedimentos operacionais, políticas, manuais e fluxogramas. Com registro de ciência.</p>
        </div>
        {isAdmin && (
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button className="btn-navy" data-testid="library-new-btn"><Plus className="w-4 h-4 mr-2" />Novo documento</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Novo documento</DialogTitle>
                <DialogDescription>Cadastre POPs, políticas, manuais ou fluxogramas para sua empresa.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title-input" /></div>
                <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tipo</Label>
                    <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                      <option value="pop">POP</option><option value="procedure">Procedimento</option><option value="flowchart">Fluxograma</option><option value="policy">Política</option><option value="manual">Manual</option><option value="presentation">Apresentação</option>
                    </select>
                  </div>
                  <div><Label>Versão</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
                </div>
                <Button className="btn-navy w-full" onClick={createDoc} data-testid="doc-save-btn">Salvar</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </header>

      {items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-600"><LibIcon className="w-10 h-10 mx-auto text-slate-400 mb-3" />Nenhum documento publicado.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((d) => (
            <Card key={d.id} className="border-[var(--unigr-border)]">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <FileText className="w-6 h-6 text-[var(--unigr-navy)]" />
                  <Badge variant="outline" className="text-[10px]">{d.type.toUpperCase()}</Badge>
                </div>
                <div className="font-display font-bold mt-3">{d.title}</div>
                <div className="text-xs text-slate-500 mt-1">Versão {d.version}</div>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{d.description}</p>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setOpenDoc(d)} data-testid={`view-doc-${d.id}`}><Eye className="w-4 h-4 mr-2" />Visualizar</Button>
                  {acknowledged(d.id) ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">Ciência registrada</Badge>
                  ) : (
                    <Button className="btn-red" onClick={() => setOpenDoc(d)} data-testid={`ack-doc-${d.id}`}><ShieldCheck className="w-4 h-4 mr-2" />Dar ciência</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!openDoc} onOpenChange={(o) => !o && setOpenDoc(null)}>
        <DialogContent className="max-w-2xl">
          {openDoc && (
            <>
              <DialogHeader>
                <DialogTitle>{openDoc.title}</DialogTitle>
                <DialogDescription>Tipo: {openDoc.type} · Versão {openDoc.version}</DialogDescription>
              </DialogHeader>
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{openDoc.description || "Sem conteúdo descritivo. Carregue o arquivo na próxima versão."}</div>
              {!acknowledged(openDoc.id) && (
                <div className="mt-4 border-t pt-4">
                  <div className="text-sm text-slate-700 mb-3">
                    <strong>Declaro</strong> que li e estou ciente deste procedimento.
                  </div>
                  <Button className="btn-navy" onClick={() => acknowledge(openDoc.id)} data-testid="confirm-ack-btn">Confirmar ciência</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
